import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Collapse,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import { CloseButton } from "components/CloseButton";
import {
  YANDEX_TTS_VOICES,
  YANDEX_TTS_FORMATS,
  YANDEX_TTS_SPEED_MIN,
  YANDEX_TTS_SPEED_MAX,
  ORDER_PICKING_VOICE_BROADCAST_SESSION_ID,
  dispatchSkipVoiceRelayPlayback,
  synthesizeVoiceRequest,
  createVoiceShareLinkBody,
  playAudioArrayBuffer,
  waitForVoiceMercurePlayback,
} from "services/apiVoice";
import { getMercureHubUrl } from "services/apiMercure";

import "./voiceTestModal.scss";

const DEFAULT_TEXT =
  "Проверка озвучки. Это тестовая фраза для сервиса синтеза речи Yandex SpeechKit.";

const mimeForFormat = (format) => {
  if (format === "mp3") return "audio/mpeg";
  if (format === "lpcm") return "audio/pcm";
  return "audio/ogg";
};

export const VoiceTestModal = ({ isOpen, toggle }) => {
  const [text, setText] = useState(DEFAULT_TEXT);
  const [voice, setVoice] = useState("alena");
  const [format, setFormat] = useState("oggopus");
  const [speed, setSpeed] = useState(1);
  const [busy, setBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [error, setError] = useState("");

  const canMercure = useMemo(() => Boolean(getMercureHubUrl()), []);

  const runPlay = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      let relayId = null;
      const useRelay = canMercure;
      if (useRelay) {
        relayId =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `r-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        dispatchSkipVoiceRelayPlayback(relayId);
      }

      const ab = await synthesizeVoiceRequest({
        text,
        voice,
        format,
        speed,
        ...(useRelay
          ? {
              broadcastSessionId: ORDER_PICKING_VOICE_BROADCAST_SESSION_ID,
              relayId,
            }
          : {}),
      });
      await playAudioArrayBuffer(ab, mimeForFormat(format));
      toast.success(
        useRelay
          ? "Воспроизведение на этом устройстве; эфир на другие вкладки страницы"
          : "Воспроизведение запущено"
      );
    } catch (e) {
      const msg = e.message || String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [text, voice, format, speed, canMercure]);

  const runShareLink = useCallback(async () => {
    setError("");
    setBusy(true);
    setShareUrl("");
    try {
      const data = await createVoiceShareLinkBody({ text, voice, format, speed });
      setShareUrl(data.streamUrl || "");
      toast.success("Ссылка получена — можно скопировать");
    } catch (e) {
      const msg = e.message || String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [text, voice, format, speed]);

  const runMercureSelfTest = useCallback(async () => {
    if (!canMercure) {
      toast.error("Mercure не настроен (REACT_APP_MERCURE_URL / hub)");
      return;
    }
    setError("");
    setBusy(true);
    const sessionId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `s-${Date.now()}`;
    try {
      const ab = await waitForVoiceMercurePlayback(
        sessionId,
        synthesizeVoiceRequest({ text, voice, format, broadcastSessionId: sessionId, speed })
      );
      await playAudioArrayBuffer(ab, mimeForFormat(format));
      toast.success("Трансляция Mercure собрана и воспроизведена");
    } catch (e) {
      const msg = e.message || String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [text, voice, format, speed, canMercure]);

  const copyShare = useCallback(() => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("Скопировано"),
      () => toast.error("Не удалось скопировать")
    );
  }, [shareUrl]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="voice-test-modal">
      <ModalHeader>
        <span className="modal-title-text">Тест голоса (Yandex SpeechKit)</span>
        <CloseButton onClick={toggle} />
      </ModalHeader>
      <ModalBody className="pb-4">
        <div className="voice-test-modal__help">
          <strong>Как пользоваться.</strong> На бэкенде должны быть заданы{" "}
          <code>YANDEX_SPEECHKIT_API_KEY</code> и <code>YANDEX_SPEECHKIT_FOLDER_ID</code>.
          Опционально в теле запроса передаётся <code>speed</code> (от {YANDEX_TTS_SPEED_MIN} до{" "}
          {YANDEX_TTS_SPEED_MAX}, обычно 1.0 — нормальный темп). Кнопка «Воспроизвести» вызывает{" "}
          <code>POST /api/v2/voice/synthesize</code> и проигрывает ответ в браузере. «Ссылка для другого
          устройства» создаёт короткую JWT-ссылку{" "}
          <code>GET /api/v2/voice/shareStream</code> — откройте её на другом устройстве в том же
          окружении (без входа в админку в течение срока жизни токена). Режим Mercure дублирует те же
          байты аудио в топик <code>voice-stream-…</code> для подписчиков (второй экран / колонка).
          При настроенном Mercure кнопка «Воспроизвести» дополнительно вещает на общий канал страницы
          «Заявки на сборку»: звук идёт у нажавшего и у всех остальных открытых вкладок с этой страницей.
        </div>

        <Button color="link" className="p-0 mb-2" onClick={() => setHelpOpen((v) => !v)}>
          {helpOpen ? "Скрыть подсказку по разработке" : "Подсказка по разработке"}
        </Button>
        <Collapse isOpen={helpOpen}>
          <pre className="small bg-light p-3 rounded">
            {`// Прямое воспроизведение (speed опционально, 0.1–3)
await playAudioArrayBuffer(
  await synthesizeVoiceRequest({ text, voice: 'alena', format: 'oggopus', speed: 1.15 })
);

// Другой браузер: POST shareLink → открыть streamUrl
const { streamUrl } = await createVoiceShareLinkBody({ text, voice, format, speed: 1.15 });`}
          </pre>
        </Collapse>

        <FormGroup>
          <Label>Текст</Label>
          <Input
            type="textarea"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
          />
        </FormGroup>

        <FormGroup>
          <Label>Голос</Label>
          <Input
            type="select"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            disabled={busy}
          >
            {YANDEX_TTS_VOICES.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label>Формат</Label>
          <Input
            type="select"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            disabled={busy}
          >
            {YANDEX_TTS_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label>
            Скорость речи: <strong>{speed.toFixed(2)}</strong> (по умолчанию у API обычно 1)
          </Label>
          <Input
            type="range"
            min={YANDEX_TTS_SPEED_MIN}
            max={YANDEX_TTS_SPEED_MAX}
            step={0.05}
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            disabled={busy}
          />
        </FormGroup>

        <div className="voice-test-modal__actions">
          <Button color="primary" onClick={runPlay} disabled={busy || !text.trim()}>
            Воспроизвести здесь
          </Button>
          <Button
            color="secondary"
            outline
            onClick={runShareLink}
            disabled={busy || !text.trim()}
          >
            Ссылка для другого устройства
          </Button>
          <Button
            color="info"
            outline
            onClick={runMercureSelfTest}
            disabled={busy || !text.trim() || !canMercure}
            title={!canMercure ? "Нет REACT_APP_MERCURE_URL" : ""}
          >
            Тест Mercure (эта вкладка)
          </Button>
        </div>

        {shareUrl && (
          <div className="voice-test-modal__share-row">
            <Input
              readOnly
              className="voice-test-modal__share-input"
              value={shareUrl}
              onFocus={(e) => e.target.select()}
            />
            <Button color="secondary" outline size="sm" onClick={copyShare}>
              Копировать
            </Button>
          </div>
        )}

        <p className="voice-test-modal__hint">
          Ссылка для второго устройства действует около 10 минут. Для воспроизведения на телефоне
          удобен Safari/Chrome по прямой ссылке или сохранение файла из ответа.
        </p>

        {error && <div className="voice-test-modal__err">{error}</div>}
      </ModalBody>
    </Modal>
  );
};
